import {
    Injectable,
    ApplicationRef,
    Injector,
    Type,
    EmbeddedViewRef,
    ComponentRef,
    createComponent,
    EnvironmentInjector,
    inject
} from '@angular/core';

/**
 * @ngdoc service
 * @name ApContentInjectionService
 *
 * @description
 * A service to dynamically inject components into the dom from typescript
 */
@Injectable({
    providedIn: 'root'
})
export class ApComponentInjectionService {
    private readonly appRef = inject(ApplicationRef);
    private readonly injector = inject(Injector);
    private environmentInjector = inject(EnvironmentInjector);

    /**
     * @description This method can be used to inject a component into the dom during runtime.
     * @param type - The type of the component you want to inject
     * @param args - A map containing the properties of the component and their corresponding values, these will be set
     * @returns an object containing both the element and the componentRef. The element can be used to inject into the dom,
     * the componentRef can be saved to detach the component later
     */
    public injectComponent<T>(type: Type<T>, args?: Map<string, any>): { element: HTMLElement; componentRef: ComponentRef<T> } {
        const componentRef = createComponent(type, {
            elementInjector: this.injector,
            environmentInjector: this.environmentInjector
        });
        if (args) {
            args.forEach((value, key) => {
                componentRef.setInput(key, value);
            });
        }
        this.appRef.attachView(componentRef.hostView);
        return {
            element: (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement,
            componentRef
        };
    }

    /**
     * @description Removes the component from the DOM and detaches it from the appRef so it will no longer listen to events
     * @param component A componentRef of the component to be removed
     */
    public removeComponent(component: ComponentRef<any>): void {
        const element = (component.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
        element.parentNode.removeChild(element);
        this.appRef.detachView(component.hostView);
        component.destroy();
    }
}
